import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

export interface DeathAction {
  department: string;
  action_label: string;
  detail: string | null;
  due_within_days: number;
  is_critical: boolean;
}

export interface DischargeEffect {
  effect: string;
  detail: string;
}

export interface Ending {
  legal_status: string | null;
  date_of_death: string | null;
  discharged_on: string | null;
  /** The server decides. No death for someone deceased; no discharge for either. */
  may_record_death: boolean;
  may_record_discharge: boolean;
  death_actions: DeathAction[];
  discharge_effects: DischargeEffect[];
}

/**
 * What the Advanced control may offer, and what each ending would set in
 * motion — one request, made only when the control is opened.
 *
 * `enabled` is the whole point: this is never fetched for a client somebody is
 * simply reading. Opening the control is the first of the deliberate steps.
 */
export function useEnding(clientId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'ending'],
    queryFn: () => apiFetch<Ending>(`/clients/${clientId}/ending`, getToken),
    ...queryDefaults,
    enabled,
  });
}

/**
 * Both endings invalidate everything about the client, and the lists they have
 * left: the visits board, the client list, every dashboard. A discharged or
 * deceased client is gone from all of them, and a stale row that still says
 * otherwise is the one thing worse than a slow refresh.
 */
function useEndingMutation<TBody>(clientId: string, path: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<{ client: { legal_status: string | null } }>(
        `/clients/${clientId}/${path}`,
        getToken,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
      void queryClient.invalidateQueries({ queryKey: ['work'] });
    },
  });
}

export interface DeathInput {
  confirm_last_name: string;
  date_of_death: string;
  place_facility_id?: string;
  place_other?: string;
  informant_name?: string;
  informant_relationship?: string;
  informant_phone?: string;
  cause_of_death?: string;
  notes?: string;
}

export interface DischargeInput {
  signed_date: string;
  court_name: string;
  county: string;
  court_index_number?: string;
  judge_name?: string;
  notes?: string;
}

export const useRecordDeath = (clientId: string) =>
  useEndingMutation<DeathInput>(clientId, 'death');

export const useRecordDischarge = (clientId: string) =>
  useEndingMutation<DischargeInput>(clientId, 'discharge');
